import KimaruLoader from "./common/KimaruLoader";

interface ActionDrawerProps {
    containerData?: any;
    apiData?: any;
    isApiLoading?: boolean;
    error?: any;
    loadingComplete?: boolean;
    processingOption?: string;
    setProcessingOption?: any;
    onFinish?: any;
    isLoading?: boolean;
    selectedCategory?: string;
    showOnlyAccepted?: boolean;
}

interface StatCardProps {
    count: number;
    icon: any;
    label: string;
}

interface StepCircleProps {
    number: number;
    isActive: boolean;
}

const ActionDrawer = ({
    containerData,
    apiData,
    isApiLoading,
    error,
    loadingComplete,
    processingOption,
    setProcessingOption,
    onFinish,
    isLoading
    , selectedCategory
    , showOnlyAccepted
}: ActionDrawerProps) => {

    const categorizeRecommendations = (): { accepted: any[]; overrides: any[] } => {
        if (!apiData) return { accepted: [], overrides: [] };

        const accepted: any[] = [];
        const overrides: any[] = [];

        // Movement recommendations
        const movements = Array.isArray(apiData.movement) ? apiData.movement : [];
        movements.forEach((item: any) => {
            if (item.type === "system_recommendation") {
                accepted.push({ ...item, category: "movement" });
            } else if (item.type === "override_recommendation") {
                overrides.push({ ...item, category: "movement" });
            }
        });

        // Price recommendations
        const prices = Array.isArray(apiData.price) ? apiData.price : [];
        prices.forEach((item: any) => {
            if (item.strategy === "override") {
                overrides.push({ ...item, category: "price" });
            } else {
                accepted.push({ ...item, category: "price" });
            }
        });

        return { accepted, overrides };
    };

    const { accepted, overrides } = categorizeRecommendations();

    const formatPriceRecommendation = (item: any) => {
        const { item_name, strategy, old_price, new_price, adjustment_value } = item;

        if (strategy === "override") {
            const direction = adjustment_value < 0 ? "Decreased " : "Increased ";
            return (
                <>
                    <span className="font-semibold">{item_name}</span>: Price manually {direction.toLowerCase()}
                    from <span className="font-semibold">{old_price} Yen</span> to{" "}
                    <span className="font-semibold">{new_price} Yen</span>
                </>
            );
        }

        const isMarkdown = strategy === "markdown";
        const action = isMarkdown ? "Markdown" : "Markup";
        const direction = isMarkdown ? "Decrease" : "Increase";
        const absPercentage = Math.abs(adjustment_value);

        return (
            <>
                For <span className="font-semibold">{item_name}</span>: <span className="font-semibold">{action}</span> original price by{" "}
                <span className="font-semibold">{absPercentage}%</span>. {direction} price to{" "}
                <span className="font-semibold">{new_price} Yen</span>
            </>
        );
    };

    const formatMovementRecommendation = (item: any) => {
        return (
            <>
                For <span className="font-semibold">{item.sku_id}</span> {item.description}
            </>
        );
    };


    const renderRecommendationList = (items: any[], showCheckmark: boolean = true) => {
        if (isApiLoading && !loadingComplete) {
            return (
                <div className="relative min-h-[100px]">
                    <KimaruLoader
                        wrapperClassName="absolute inset-0 flex items-center justify-center"
                        size={60}
                    />
                </div>
            );
        }

        if (error) {
            return <p className="text-sm text-gray-500">Error loading recommendations</p>;
        }

        if (items.length === 0) {
            return <p className="text-sm text-gray-500">No recommendations to display</p>;
        }

        return (
            <div className="space-y-3">
                {items.map((item: any) => (
                    <div key={item.rec_id} className="flex items-start gap-2">
                        {showCheckmark && (
                            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path
                                        d="M13.3337 4L6.00033 11.3333L2.66699 8"
                                        stroke="#111827"
                                        strokeWidth="1.33"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </div>
                        )}
                        <span className="text-sm text-gray-700">
                            {item.category === "movement"
                                ? formatMovementRecommendation(item)
                                : formatPriceRecommendation(item)}
                        </span>
                    </div>
                ))}
            </div>
        );
    };

    const StatCard = ({ count, icon, label }: StatCardProps) => (
        <div className="flex flex-col items-center">
            <div className="text-4xl font-bold text-gray-900 mb-1">{count}</div>
            <div className="flex items-center gap-2">
                {icon}
                <span className="text-sm font-medium text-gray-700">{label}</span>
            </div>
        </div>
    );

    const thumbsUpIcon = (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
                d="M4.66634 6.66659V14.6666M9.99967 3.91992L9.33301 6.66659H13.2197C13.4267 6.66659 13.6308 6.71478 13.816 6.80735C14.0011 6.89992 14.1621 7.03432 14.2863 7.19992C14.4105 7.36551 14.4945 7.55775 14.5315 7.7614C14.5685 7.96506 14.5576 8.17454 14.4997 8.37325L12.9463 13.7066C12.8656 13.9835 12.6971 14.2268 12.4663 14.3999C12.2355 14.573 11.9548 14.6666 11.6663 14.6666H2.66634C2.31272 14.6666 1.97358 14.5261 1.72353 14.2761C1.47348 14.026 1.33301 13.6869 1.33301 13.3333V7.99992C1.33301 7.6463 1.47348 7.30716 1.72353 7.05711C1.97358 6.80706 2.31272 6.66659 2.66634 6.66659H4.50634C4.7544 6.66645 4.9975 6.59713 5.20831 6.4664C5.41912 6.33567 5.58929 6.14873 5.69967 5.92659L7.99967 1.33325C8.31406 1.33715 8.6235 1.41203 8.90488 1.55232C9.18625 1.6926 9.43229 1.89466 9.62461 2.14339C9.81693 2.39212 9.95055 2.68109 10.0155 2.98872C10.0804 3.29635 10.075 3.61468 9.99967 3.91992Z"
                stroke="#000000ff"
                strokeWidth="1.33"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );

    const overrideIcon = (
        <svg width="16" height="22" viewBox="0 0 16 22" fill="none">
            <rect
                width="7.16272" height="7.16272"
                transform="matrix(0.705409 0.708801 -0.705409 0.708801 5.05273 0)"
                fill="#3162B4"
            />
            <rect
                width="7.16272" height="7.16272"
                transform="matrix(0.705409 0.708801 -0.705409 0.708801 10.9473 5.9231)"
                fill="#5215A3"
            />
            <rect
                width="7.16272" height="7.16272"
                transform="matrix(0.705409 0.708801 -0.705409 0.708801 5.05273 11.8462)"
                fill="#3A7575"
            />
        </svg>
    );

    const clockIcon = (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
                d="M8 2.66663V8L10.6667 10.6666M14.6667 8C14.6667 11.6819 11.6819 14.6666 8 14.6666C4.31814 14.6666 1.33337 11.6819 1.33337 8C1.33337 4.31805 4.31814 1.33329 8 1.33329C11.6819 1.33329 14.6667 4.31805 14.6667 8Z"
                stroke="#000000ff"
                strokeWidth="1.33"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );

    const StepCircle = ({ number, isActive }: StepCircleProps) => (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" className={`${isActive ? 'opacity-100' : 'opacity-50'}`}>
            <circle cx="12" cy="12" r="11" fill="#F1F5F9" stroke="#64748B" strokeWidth="2" />
            <text x="12" y="16" textAnchor="middle" fontSize="12" fontWeight={isActive ? "600" : "400"} fill="#64748B">
                {number}
            </text>
        </svg>
    );

    const getItemsForCategory = (): { title: string; items: any[] } => {
        const { accepted = [], overrides = [] } = categorizeRecommendations();
        if (selectedCategory === 'accepted') return { title: 'Accepted Recommendations', items: accepted };
        if (selectedCategory === 'adjusted') return { title: 'Adjusted Recommendations', items: overrides };
        return { title: 'Deferred Recommendations', items: [] };
    };

    const { title: sectionTitle, items: sectionItems } = getItemsForCategory();

    const getDeferredItems = (): any[] => {
        if (!apiData) return [];
        const movement = apiData.deferredMovement || [];
        const price = apiData.deferredPrice || [];
        const normalizedMovement = movement.map((it: any, idx: number) => ({ ...it, rec_id: it.rec_id || `d-m-${idx}`, category: 'movement' }));
        const normalizedPrice = price.map((it: any, idx: number) => ({ ...it, rec_id: it.rec_id || `d-p-${idx}`, category: 'price' }));
        return [...normalizedMovement, ...normalizedPrice];
    };

    const deferredItems: any[] = apiData?.deferredDetails?.length ? (apiData.deferredDetails.map((it: any, idx: number) => ({ ...it, rec_id: it.rec_id || `d-det-${idx}`, category: it.recommendation_type === 'movement' ? 'movement' : 'price' }))) : getDeferredItems();

    if (selectedCategory) {
        const title = selectedCategory === 'accepted' ? 'Accepted Recommendations' : selectedCategory === 'adjusted' ? 'Recommendation Overrides' : 'Deferred Recommendations';
        const icon = selectedCategory === 'accepted' ? thumbsUpIcon : selectedCategory === 'adjusted' ? overrideIcon : clockIcon;
        return (
            <div className="h-full">
                <div className="mb-4 flex items-center gap-2">
                    <div>{icon}</div>
                    <h4 className="text-lg font-semibold text-gray-900">{title}</h4>
                </div>
                <div>
                    {selectedCategory === 'deferred' ? renderRecommendationList(deferredItems) : renderRecommendationList(selectedCategory === 'accepted' ? accepted : overrides)}
                </div>
            </div>
        );
    }

    return (
        <div className="bg-slate-50 flex flex-col h-full">
            <div className="flex-1 overflow-y-auto p-6 pb-4">
                    {/* Container Header */}
                    <div className="bg-white border border-gray-200 rounded-lg mb-6">
                        <div className="flex p-2 px-3 justify-between items-center border-b border-gray-200 bg-slate-50 rounded-t-lg">
                            <h3 className="text-lg font-semibold text-gray-900">
                                Container #{containerData?.container_id || "003"}
                            </h3>
                            <p className="text-sm text-gray-500">
                                {containerData?.location || "Asia Pacific > Japan > Miyazaki"}
                            </p>
                        </div>

                        <div className="flex h-[120px] p-3 px-4 justify-center items-center gap-12">
                            <StatCard count={accepted.length} icon={thumbsUpIcon} label="Accepted" />
                            <StatCard count={overrides.length} icon={overrideIcon} label="Overrides" />
                            <StatCard count={apiData?.deferredCount || 0} icon={clockIcon} label="Deferred" />
                        </div>
                    </div>

                    {/* Processing Options */}
                    <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
                        <h4 className="text-lg font-semibold text-gray-900 mb-4">
                            How would you like to process the recommendations?
                        </h4>
                        <div className="space-y-3">
                            <label className="flex items-center gap-3 cursor-pointer">
                                <input
                                    type="radio"
                                    name="processOption"
                                    value="sap"
                                    className="w-4 h-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                                    checked={processingOption === "sap"}
                                    onChange={() => setProcessingOption && setProcessingOption("sap")}
                                />
                                <span className="text-gray-900">Send to S/4 Hana (SAP)</span>
                            </label>
                            <label className="flex items-center gap-3 cursor-pointer">
                                <input
                                    type="radio"
                                    name="processOption"
                                    value="csv"
                                    className="w-4 h-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                                    checked={processingOption === "csv"}
                                    onChange={() => setProcessingOption && setProcessingOption("csv")}
                                />
                                <span className="text-gray-900">Download CSV</span>
                            </label>
                        </div>
                    </div>

                    {/* Recommendation Sections */}
                    <div className="space-y-6">
                        <div className="bg-white border border-gray-200 rounded-lg p-6">
                            <div className="flex items-center gap-2 mb-4">
                                {sectionTitle.includes('Accepted') ? thumbsUpIcon : sectionTitle.includes('Overrides') ? overrideIcon : clockIcon}
                                <h4 className="text-lg font-semibold text-gray-900">{sectionTitle}</h4>
                            </div>
                            {selectedCategory === 'deferred' ? (
                                renderRecommendationList(deferredItems)
                            ) : (
                                renderRecommendationList(sectionItems)
                            )}
                        </div>
                    </div>
                </div>
            </div>
    );
};

export default ActionDrawer;
